import { ref, computed, useContext, ssrRef } from "@nuxtjs/composition-api";
import {
  REQUEST_TENDER_PAGE_MODULES,
  DEFAULT_REQUEST_TENDER_PAGE_MODULES,
  getBaseSlug,
} from "~/config/requestTenderPageModules";

const isCustomQuestionModule = (mod) =>
  mod?.content && mod.content.type === "custom-question";

const isRenderableModule = (mod) => {
  if (!mod) return false;
  if (isCustomQuestionModule(mod)) return true;
  const slug = mod.slug;
  return (
    slug in REQUEST_TENDER_PAGE_MODULES ||
    getBaseSlug(slug) in REQUEST_TENDER_PAGE_MODULES
  );
};

const normalizeProfessionId = (professionId) =>
  professionId != null && professionId !== "" ? String(professionId) : null;

/**
 * Shared CORE state: only plain refs + plain functions live here.
 *
 * IMPORTANT: no `computed()` / `watch()` may be created in the core. Those
 * register a reactive effect against the *effect scope of the component that
 * first instantiated the core*. Because the core is a client-side singleton
 * (shared across the wizard's per-step component remounts), that first
 * component eventually unmounts — which would dispose the effect and FREEZE
 * the derived value at its last cached result, so it would stop tracking
 * `modules` after the first navigation. Refs have no such scope binding, so
 * the raw state is safe to share; derived values are built per-consumer in
 * `useRequestTenderPageModules()` below, inside each component's own scope.
 */
function createCore() {
  const { $query, $logger } = useContext();

  const modules = ssrRef([], "requestTenderPageModules");
  const versionId = ssrRef(null, "requestTenderPageVersionId");
  const isOverride = ssrRef(false, "requestTenderPageIsOverride");
  // Which profession the current config was resolved FOR (null = global).
  // Lets consumers detect that the loaded config no longer matches the
  // selected profession (e.g. after a remount) and re-resolve.
  const resolvedForProfessionId = ssrRef(
    null,
    "requestTenderPageResolvedForProfessionId"
  );
  const loading = ref(false);
  // ssrRef so a server-side resolve() flags the config as loaded on the
  // client too — otherwise the client would re-show the loader and refetch
  // even though the modules were already rendered into the SSR HTML.
  const loaded = ssrRef(false, "requestTenderPageLoaded");
  // Whether the last resolve produced a DEFINITIVE answer (a real config, or a
  // genuine "no config" null) rather than a transient fetch failure. A failure
  // must not be cached as success, or the useFetch guard would never retry it.
  const resolvedOk = ssrRef(true, "requestTenderPageResolvedOk");

  const loadFromApi = async ({ professionId, professionSeoName } = {}) => {
    const GET_CONFIG = await import(
      "~/components/_refactored/conductor/request-tender-page-modules/getActiveRequestTenderPageConfig.graphql"
    );
    const result = await $query(
      GET_CONFIG.default || GET_CONFIG,
      {
        professionId: professionId || null,
        professionSeoName: professionSeoName || null,
      },
      {
        showErrorPage: false,
        showErrorToast: false,
      }
    );

    if (result?.activeRequestTenderPageConfig) {
      versionId.value = result.activeRequestTenderPageConfig.versionId;
      isOverride.value = !!result.activeRequestTenderPageConfig.isOverride;
      modules.value = result.activeRequestTenderPageConfig.modules.map((m) => ({
        ...m,
        versionId: result.activeRequestTenderPageConfig.versionId,
      }));
      return true;
    }
    return false;
  };

  const loadDefaults = () => {
    versionId.value = null;
    isOverride.value = false;
    modules.value = DEFAULT_REQUEST_TENDER_PAGE_MODULES.map((m) => ({
      ...m,
      versionId: null,
    }));
  };

  const resolve = async ({ professionId, professionSeoName } = {}) => {
    loading.value = true;
    // A definitive answer = the backend returned a config OR a genuine null (no
    // active config → the local defaults are the correct fallback). Only a
    // thrown query is non-definitive: it must stay retryable instead of
    // sticking the whole session on the default modules.
    let definitive = true;
    try {
      const ok = await loadFromApi({ professionId, professionSeoName });
      if (!ok) {
        loadDefaults();
        if (professionId != null) {
          $logger?.info(
            "No active request-tender config for profession; using defaults",
            { professionId: String(professionId) }
          );
        }
      }
    } catch (err) {
      definitive = false;
      $logger?.error("Failed to load request-tender page config", {
        professionId: professionId != null ? String(professionId) : null,
        professionSeoName: professionSeoName ?? null,
        err,
      });
      loadDefaults();
    } finally {
      resolvedOk.value = definitive;
      resolvedForProfessionId.value = normalizeProfessionId(professionId);
      loading.value = false;
      loaded.value = true;
    }
  };

  const getComponent = (slug) => {
    const loader =
      REQUEST_TENDER_PAGE_MODULES[slug] ||
      REQUEST_TENDER_PAGE_MODULES[getBaseSlug(slug)];
    if (!loader) {
      $logger?.warn(`Unknown request-tender page module slug: ${slug}`);
      return null;
    }
    return () => loader();
  };

  return {
    modules,
    versionId,
    isOverride,
    resolvedForProfessionId,
    loading,
    loaded,
    resolvedOk,
    resolve,
    loadDefaults,
    getComponent,
  };
}

let clientCore = null;

const getCore = () => {
  // Client: one shared core so a profession-specific config resolved in the
  // wizard's first step survives the per-step component remounts (the page is
  // re-created on every `activeTab` change). Server: fresh per request so
  // state never leaks between SSR requests.
  if (process.client && clientCore) {
    return clientCore;
  }
  const core = createCore();
  if (process.client) {
    clientCore = core;
  }
  return core;
};

/**
 * Loads the active request-tender page module config for the given profession.
 *
 * Resolution (server-side, see backend spec):
 *   1. profession-specific override if active
 *   2. global default if active
 *   3. null → fall back to local DEFAULT_REQUEST_TENDER_PAGE_MODULES
 *
 * Unlike the front/profession page composables, this loader is intentionally
 * imperative — it's called on demand once the user picks a profession in the
 * wizard's fixed first step (or eagerly when the profession is known from the
 * URL/cookie).
 */
export default function useRequestTenderPageModules() {
  const core = getCore();

  // Built per-consumer (NOT in the shared core) so the reactive effect is
  // owned by the calling component's scope and stays live for as long as that
  // component is mounted — see the note on createCore().
  const visibleModules = computed(() =>
    core.modules.value
      .filter((m) => m.isVisible && isRenderableModule(m))
      .sort((a, b) => a.position - b.position)
  );

  return {
    ...core,
    visibleModules,
  };
}
